import * as df from 'date-fns';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import BooksDateCalendarHeader from '../common/calendar/BooksDateCalendarHeader';
import BooksDateCalendarMonth, {
  BooksDateCalendarDay,
} from '../common/calendar/BooksDateCalendarMonth';
import BooksSelectTimeAvailability from '../common/inputs/BooksSelectTimeAvailability';
import LabeledContainer from '../../../../common/LabeledContainer';

import {
  AvailableDates,
  AvailabilityState,
  AvailableTimes,
  EventAvailability,
  TimeSlotAvailability,
} from '../../../../../interfaces/merchants/resources/books';

import css from './InputBookingDateTimeCalendar.module.scss';

import { getCalendarDate } from '../../../../../helpers/merchants/resources/helper';
import { TimezoneContext } from '../../../../../contexts/TimezoneContext';
import { dateStringToZonedTime } from '../../../calendar/calendarHelper';

interface Props {
  postable: boolean;
  baseDate: Date;
  onSubmit: (availability: TimeSlotAvailability) => void;
  onChangeBaseDate: (baseDate: Date) => void;
  onChangeSelectedDate: (selectedDate: Date) => void;
  selectedDate?: string;
  availableDates: AvailableDates;
  availableTimes: AvailableTimes;
}

interface DateType {
  date: number;
  iso: string;
  type: string;
}

interface Calendar {
  baseDate: Date;
  dates: DateType[];
}

const isAvailable = (date: string, dates: string[]) => {
  let exists = false;
  for (const d of dates) {
    if (d === date) {
      exists = true;
      break;
    }
  }
  return exists;
};

const InputBookingDateTimeCalendar: React.FC<Props> = (props) => {
  const context = useContext(TimezoneContext);
  const intl = useIntl();

  const [calendar, setCalendar] = useState<Calendar>({
    baseDate: props.baseDate,
    dates: [],
  });

  useEffect(() => {
    const init = async () => {
      await fillCalendarPropsBy(calendar.baseDate);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fillCalendarPropsBy = useCallback(
    async (baseDate: Date) => {
      const { selectedDate } = props;

      const dates = getCalendarDate(baseDate);
      setCalendar({ baseDate, dates });
      props.onChangeBaseDate(baseDate);
      if (selectedDate) {
        props.onChangeSelectedDate(
          dateStringToZonedTime(selectedDate, context.timezone).toJSDate(),
        );
      }
    },
    [context.timezone, props],
  );

  const handleClickNext = useCallback(() => {
    const { baseDate } = calendar;
    fillCalendarPropsBy(df.addMonths(baseDate, 1));
  }, [calendar, fillCalendarPropsBy]);

  const handleClickPrev = useCallback(() => {
    const { baseDate } = calendar;
    fillCalendarPropsBy(df.subMonths(baseDate, 1));
  }, [calendar, fillCalendarPropsBy]);

  const handleClickDate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { value } = e.currentTarget;
      if (props.selectedDate === value) {
        return;
      }
      props.onChangeSelectedDate(
        dateStringToZonedTime(value, context.timezone).toJSDate(),
      );
    },
    [props, context.timezone],
  );

  const handleClickTime = useCallback(
    (availability: EventAvailability) => {
      const { selectedDate, onSubmit } = props;
      onSubmit({ date: selectedDate!, ...availability });
    },
    [props],
  );

  const { availableDates, availableTimes, selectedDate, postable } = props;
  const candidates = selectedDate ? availableTimes.availabilities : [];

  return (
    <div className={css.InputBookingDateTimeCalendar}>
      <BooksDateCalendarHeader
        baseDate={intl.formatDate(df.format(calendar.baseDate), {
          year: 'numeric',
          month: 'long',
          day: undefined,
        })}
        onClickPrev={handleClickPrev}
        onClickNext={handleClickNext}
        prevButtonDisabled={df.isThisMonth(calendar.baseDate)}
      />
      <BooksDateCalendarMonth>
        {calendar.dates.map((date, index) => (
          <BooksDateCalendarDay
            key={index}
            value={date.iso}
            date={date.date}
            onClick={handleClickDate}
            disabled={
              !(availableDates && isAvailable(date.iso, availableDates.dates))
            }
            current={date.type === 'current'}
            selected={selectedDate === date.iso}
          />
        ))}
      </BooksDateCalendarMonth>
      {candidates.length > 0 ? (
        <LabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.booksTime',
          })}
        >
          {candidates.map((availability: EventAvailability) => (
            <BooksSelectTimeAvailability
              key={availability.time}
              value={availability.value}
              onClick={() => handleClickTime(availability)}
              reservable={availability.state === AvailabilityState.Available}
              postable={postable}
            />
          ))}
        </LabeledContainer>
      ) : (
        <LabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.booksTime',
          })}
          contentsPadding={true}
        >
          <span className={css.InputBookingDateTimeCalendar__PlaceHolder}>
            {intl.formatMessage({
              id: 'books.forms.pleaseSelectDate',
            })}
          </span>
        </LabeledContainer>
      )}
    </div>
  );
};

export default InputBookingDateTimeCalendar;
